import { useCallback, useEffect, useState } from "react";
import useToast from "~/components/ui/toast";
import {
  NEXTGEN_CHAT_EVENT,
  trackNextgenChatEvent,
} from "~/pages/manifold-nextgen/components/conversations/analytics";
import { nextgenSharedConversationPath } from "~/pages/manifold-nextgen/paths";
import {
  type ConversationShare,
  createShareLink,
  deleteShareLink,
  getShareLink,
} from "~/services/conversations";

const buildShareUrl = (token: string) =>
  `${window.location.origin}${nextgenSharedConversationPath(token)}`;

export interface UseConversationShare {
  // `undefined` = haven't fetched yet; `null` = fetched, no share exists;
  // `ConversationShare` = fetched, share exists.
  share: ConversationShare | null | undefined;
  shareUrl: string | null;
  isMutating: boolean;
  copied: boolean;
  // Returns the created (or already-existing, since the endpoint is
  // idempotent) share, or null if generation failed — callers that need to
  // know whether a link now exists (e.g. featuring, which requires one)
  // should check the return value rather than re-reading `share`, since a
  // state update from this same call hasn't necessarily flushed yet.
  handleGenerate: () => Promise<ConversationShare | null>;
  // Returns whether the revoke actually succeeded — callers that cascade an
  // action off of it (e.g. unfeaturing) need to know it didn't silently no-op
  // on failure, since the error here is already reported via toast.
  handleRevoke: () => Promise<boolean>;
  handleCopy: () => Promise<void>;
}

// Share-link state machine for a single conversation. `active` mirrors the
// open state of whichever surface hosts it (dialog or popover): the share
// status is (re)fetched each time the surface opens, and the copied flag is
// reset when it closes.
export const useConversationShare = (
  conversationId: string,
  active: boolean
): UseConversationShare => {
  const { showError, showSuccess } = useToast();
  const [share, setShare] = useState<ConversationShare | null | undefined>(undefined);
  const [isMutating, setIsMutating] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!active) {
      setCopied(false);
      return;
    }
    setShare(undefined);
    let cancelled = false;
    void (async () => {
      try {
        const existing = await getShareLink(conversationId);
        if (!cancelled) setShare(existing);
      } catch {
        if (!cancelled) {
          setShare(null);
          showError("Couldn't load share status");
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // showError is intentionally excluded: the toast provider doesn't memoize it,
    // so emitting a toast gives a new identity and would re-fire this fetch in a loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, conversationId]);

  const handleGenerate = useCallback(async () => {
    setIsMutating(true);
    try {
      const created = await createShareLink(conversationId);
      setShare(created);
      trackNextgenChatEvent(NEXTGEN_CHAT_EVENT.CONVERSATION_SHARED, { conversationId });
      return created;
    } catch {
      showError("Couldn't create share link");
      return null;
    } finally {
      setIsMutating(false);
    }
  }, [conversationId, showError]);

  const handleRevoke = useCallback(async () => {
    setIsMutating(true);
    try {
      await deleteShareLink(conversationId);
      setShare(null);
      setCopied(false);
      showSuccess("Share link revoked");
      return true;
    } catch {
      showError("Couldn't revoke share link");
      return false;
    } finally {
      setIsMutating(false);
    }
  }, [conversationId, showError, showSuccess]);

  const handleCopy = useCallback(async () => {
    if (!share) return;
    try {
      await navigator.clipboard.writeText(buildShareUrl(share.token));
      setCopied(true);
    } catch {
      showError("Couldn't copy the link");
    }
  }, [share, showError]);

  useEffect(() => {
    if (!copied) return;
    const id = window.setTimeout(() => setCopied(false), 2000);
    return () => window.clearTimeout(id);
  }, [copied]);

  return {
    share,
    shareUrl: share ? buildShareUrl(share.token) : null,
    isMutating,
    copied,
    handleGenerate,
    handleRevoke,
    handleCopy,
  };
};
